import NA60Validator from 'shared/ncts/EE/NA60'
import NA61Validator from 'shared/ncts/EE/NA61'
import NA62Validator from 'shared/ncts/EE/NA62'
import { AmountUnit } from 'features/declaration/consignment-item/models/schema'
import { hasText } from 'shared/lib/common-util'
import { FieldRequirementResolverContext } from '../types'
import { createFieldRequirementGetter, matchServerRequirement } from '../../../resolverFactory'
import {
  FieldRequirement,
  FieldRequirementEnvironment,
  FieldRequirementResolverMap,
  forbidden,
  optional,
  required,
  toFieldRequirement,
} from '../../../../../../models/fieldRules'
import C0060Validator from '../../../../../../../../shared/ncts/p5/conditions/C0060'
import C0015Validator from '../../../../../../../../shared/ncts/p5/conditions/C0015'
import CustomValidators from '../../../../../../../../shared/ncts/validation'
import C0821Validator from '../../../../../../../../shared/ncts/p5/conditions/C0821'

function ruleForAdditionalReferenceReferenceNumber(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  return toFieldRequirement(C0015Validator.validateConsignmentItemAdditionalReferenceRefNumberRequirement(ctx.additionalReference))
}

function ruleForPreviousDocumentDocumentType(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  if (ctx.country !== 'EE') return 'required'

  return toFieldRequirement(NA60Validator.validatePreviousDocumentDocumentTypeRequirement(ctx.previousDocument))
}

function ruleForPreviousDocumentProcedureType(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  if (ctx.country !== 'EE') return 'forbidden'

  const documentTypeRequirement = ruleForPreviousDocumentDocumentType(env)
  if (documentTypeRequirement === 'forbidden') return 'optional'

  return toFieldRequirement(NA60Validator.validatePreviousDocumentDocumentTypeRequirement(ctx.previousDocument))
}

function ruleForPreviousDocumentReferenceNumber(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  if (ctx.country !== 'EE') return 'required'

  let requirement = toFieldRequirement(NA61Validator.validatePreviousDocumentRefNumberRequirement(ctx.previousDocument))
  if (requirement === 'optional') {
    requirement = toFieldRequirement(NA62Validator.validatePreviousDocumentReferenceNumberRequirement(ctx.previousDocument))

    if (requirement === 'forbidden' && hasText(ctx.previousDocument?.referenceNumber)) {
      requirement = 'optional'
    }
  }

  return requirement
}

function ruleForPreviousDocumentTransportDocumentReferenceNumber(
  env: FieldRequirementEnvironment<FieldRequirementResolverContext>,
): FieldRequirement {
  const { ctx } = env

  const isRequired = CustomValidators.validateTransportDocumentReferenceNumberRequirement(
    ctx.country,
    ctx.previousDocument?.documentType,
  )

  return isRequired ? 'required' : 'optional'
}

function ruleForCommodityCombinedNomenclatureCode(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  return toFieldRequirement(C0821Validator.validateCombinedNomenclatureCodeRequirement(ctx))
}

function ruleForDutyAmount(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  // TODO: first item
  return (ctx.country === 'RO' && ctx.dutyRateUnit === AmountUnit.AMOUNT) ? 'required' : 'optional'
}

function ruleForDutyRate(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  // TODO: first item
  return (ctx.country === 'RO' && ctx.dutyRateUnit !== AmountUnit.AMOUNT) ? 'required' : 'optional'
}

function ruleForPrice(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  // TODO: first item
  return ctx.country === 'RO' ? 'required' : 'optional'
}

function ruleForVatRate(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  // TODO: first item
  return ctx.country === 'RO' ? 'required' : 'optional'
}

function ruleForPackagingNumberOfPackages(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  return toFieldRequirement(C0060Validator.validatePackagingNumberOfPackagesRequirement(ctx.packaging?.typeOfPackages))
}

function ruleForPackagingShippingMarks(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  return toFieldRequirement(C0060Validator.validatePackagingShippingMarksRequirement(ctx.packaging?.typeOfPackages))
}

const fieldRequirementResolvers: FieldRequirementResolverMap<FieldRequirementResolverContext> = {
  additionalInformation: forbidden,
  additionalSupplyChainActor: forbidden,
  'additionalReference.*.type': required,
  'additionalReference.*.referenceNumber': ruleForAdditionalReferenceReferenceNumber,
  commodityDescriptionOfGoods: required,
  commodityHarmonizedSystemSubHeadingCode: optional, // overwritten by server-side rule
  commodityCombinedNomenclatureCode: ruleForCommodityCombinedNomenclatureCode, // overwritten by server-side rule
  consignee: forbidden,
  containerIndicator: forbidden,
  countryOfDestination: optional, // overwritten by server-side rule
  countryOfDispatch: optional, // overwritten by server-side rule
  differentPassiveBorderTransportMeans: forbidden,
  dutyAmount: ruleForDutyAmount,
  dutyRate: ruleForDutyRate,
  goodsMeasureNetMass: optional, // overwritten by server-side rule
  goodsMeasureGrossMass: required,
  passiveBorderTransportMeans: forbidden,
  packaging: required,
  'packaging.*.typeOfPackages': required,
  'packaging.*.numberOfPackages': ruleForPackagingNumberOfPackages,
  'packaging.*.shippingMarks': ruleForPackagingShippingMarks,
  previousDocument: optional, // overwritten by server-side rule
  'previousDocument.*.documentType': ruleForPreviousDocumentDocumentType,
  'previousDocument.*.procedureType': ruleForPreviousDocumentProcedureType,
  'previousDocument.*.referenceNumber': ruleForPreviousDocumentReferenceNumber,
  'previousDocument.*.transportDocumentReferenceNumber': ruleForPreviousDocumentTransportDocumentReferenceNumber,
  price: ruleForPrice,
  referenceNumberUcr: optional, // overwritten by server-side rule
  'supportingDocument.*.documentType': required,
  'transportDocument.*.documentType': required,
  transportEquipment: forbidden,
  vatRate: ruleForVatRate,
}

function getServerRequirementForPath(
  path: string,
  ctx: FieldRequirementResolverContext,
) {
  return matchServerRequirement(ctx.serverValidationResult?.fieldRequirements, path)
}

const getFieldRequirementP5 = createFieldRequirementGetter<FieldRequirementResolverContext>(
  fieldRequirementResolvers,
  getServerRequirementForPath,
)

export default getFieldRequirementP5
